import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AdsClickRoundedIcon from "@mui/icons-material/AdsClickRounded";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import PersonAddAlt1RoundedIcon from "@mui/icons-material/PersonAddAlt1Rounded";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import PsychologyRoundedIcon from "@mui/icons-material/PsychologyRounded";
import QueryStatsRoundedIcon from "@mui/icons-material/QueryStatsRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import RouteRoundedIcon from "@mui/icons-material/RouteRounded";
import SpeedRoundedIcon from "@mui/icons-material/SpeedRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import WebRoundedIcon from "@mui/icons-material/WebRounded";
import WebProfitSpinner from "../../components/system/WebProfitSpinner";
import { adminApi } from "../../api/admin";
import { acquisitionApi } from "../../api/acquisition";
import { useProjects } from "../../context/ProjectsContext";

// SUPERPANEL ANALYTICS · BLOCK 5 · INTELLIGENCE JOURNEY
// STATUS: STATIC_PASS · STAGING_PENDING
// PURPOSE: turn the previous row of cards into one visual flow that makes volume,
// loss, time and friction readable without scanning a long dashboard.
// STABILITY: analytics remains read-only. Existing endpoints are reused; the only
// backend addition is aggregate factor metadata under acquisition.factors.
// COHORT SAFETY: pre-registration and post-registration cohorts are never joined
// with a fake conversion. A visible boundary is rendered when the cohort changes.
// PRIVACY: factor analysis uses categorical buckets and aggregate counts only.

const BRAND = {
  orange: "#F35A3A",
  green: "#63B887",
  amber: "#ED9D45",
  red: "#E57373",
  muted: "rgba(var(--wp-panel-ink-rgb,245,245,245),.62)",
  faint: "rgba(var(--wp-panel-ink-rgb,245,245,245),.08)",
  border: "rgba(var(--wp-panel-ink-rgb,245,245,245),.10)",
  surface: "var(--wp-panel-surface, #1F2025)",
};

const PERIODS = [
  { value: 7, label: "7 días" },
  { value: 30, label: "30 días" },
  { value: 90, label: "90 días" },
  { value: 180, label: "180 días" },
];

const LENSES = [
  { key: "journey", label: "Journey" },
  { key: "acquisition", label: "Adquisición" },
  { key: "activation", label: "Activación" },
  { key: "onboarding", label: "Onboarding" },
  { key: "billing", label: "Suscripciones" },
  { key: "friction", label: "Fricción" },
];

const MODES = [
  { key: "conversion", label: "Conversión", icon: <RouteRoundedIcon sx={{ fontSize: 15 }} /> },
  { key: "time", label: "Tiempo", icon: <SpeedRoundedIcon sx={{ fontSize: 15 }} /> },
  { key: "friction", label: "Fricción", icon: <WarningAmberRoundedIcon sx={{ fontSize: 15 }} /> },
];

const ONBOARDING_ORDER = [
  "business_context",
  "site_setup",
  "catalog",
  "movements_orientation",
  "site_activation",
  "historical_data",
  "initial_stock",
  "initial_finance",
  "access_control",
  "first_sale",
  "first_purchase",
  "fiscal_setup",
  "payments_setup",
  "daily_routine",
  "billing_continuity",
];

const ONBOARDING_LABELS = {
  business_context: "Negocio",
  site_setup: "Mi web",
  catalog: "Catálogo",
  site_activation: "Publicar web",
  movements_orientation: "Movimientos",
  initial_stock: "Stock",
  historical_data: "Historia",
  initial_finance: "Caja",
  access_control: "Accesos",
  first_sale: "Venta",
  first_purchase: "Compra",
  fiscal_setup: "Facturación",
  payments_setup: "Cobros",
  daily_routine: "Rutina",
  billing_continuity: "Módulos",
};

const ICONS = {
  entry: <WebRoundedIcon fontSize="small" />,
  visitors: <WebRoundedIcon fontSize="small" />,
  intent: <AdsClickRoundedIcon fontSize="small" />,
  funnel: <PsychologyRoundedIcon fontSize="small" />,
  diagnosis: <PsychologyRoundedIcon fontSize="small" />,
  contact: <PersonAddAlt1RoundedIcon fontSize="small" />,
  account: <PersonAddAlt1RoundedIcon fontSize="small" />,
  account_post: <PersonAddAlt1RoundedIcon fontSize="small" />,
  project: <BusinessRoundedIcon fontSize="small" />,
  panel: <LoginRoundedIcon fontSize="small" />,
  item: <Inventory2RoundedIcon fontSize="small" />,
  active: <RocketLaunchRoundedIcon fontSize="small" />,
  default: <RouteRoundedIcon fontSize="small" />,
};

function num(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function ratio(part, total) {
  return total ? Math.round((num(part) / num(total)) * 1000) / 10 : 0;
}

function safeRate(value) {
  return Math.max(0, Math.min(100, num(value)));
}

function formatNumber(value) {
  return new Intl.NumberFormat("es-AR").format(num(value));
}

function formatDecimal(value) {
  return new Intl.NumberFormat("es-AR", { maximumFractionDigits: 1 }).format(num(value));
}

function formatDuration(value) {
  const ms = num(value);
  if (!ms) return "—";
  if (ms < 1000) return `${Math.round(ms)} ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${formatDecimal(seconds)} s`;
  const minutes = seconds / 60;
  if (minutes < 60) return `${formatDecimal(minutes)} min`;
  const hours = minutes / 60;
  if (hours < 24) return `${formatDecimal(hours)} h`;
  return `${formatDecimal(hours / 24)} d`;
}

function humanize(value) {
  return String(value || "—")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (char) => char.toUpperCase());
}


const PRIMARY_NEED_LABELS = {
  website: "Página web",
  stock: "Stock y movimientos",
  memberships: "Mensualidades",
  appointments: "Turnos por web",
  property_payments: "Pagos de inmuebles",
  organize_all: "Todo junto",
};

function primaryNeedLabel(value) {
  return PRIMARY_NEED_LABELS[value] || humanize(value);
}

function findStage(rows, key) {
  return (rows || []).find((row) => row.key === key) || {};
}

function toneForHealth(value) {
  const rate = safeRate(value);
  if (rate >= 70) return BRAND.green;
  if (rate >= 40) return BRAND.amber;
  return BRAND.red;
}

function toneForRisk(value) {
  const risk = safeRate(value);
  if (risk >= 65) return BRAND.red;
  if (risk >= 35) return BRAND.amber;
  return BRAND.green;
}

function withComparableRates(rows = []) {
  return rows.map((row, index) => {
    const previous = index > 0 ? rows[index - 1] : null;
    const comparable = Boolean(previous) && previous.cohort === row.cohort && row.comparable !== false && previous.comparable !== false;
    if (!comparable) {
      return {
        ...row,
        rate: row.rate !== undefined ? safeRate(row.rate) : 100,
        dropRate: row.dropRate !== undefined ? safeRate(row.dropRate) : 0,
        comparableFromPrevious: false,
      };
    }
    const previousValue = num(previous.value);
    const currentValue = num(row.value);
    const derivedRate = previousValue ? Math.min(100, ratio(currentValue, previousValue)) : 0;
    return {
      ...row,
      rate: derivedRate,
      dropRate: previousValue ? Math.max(0, ratio(previousValue - Math.min(previousValue, currentValue), previousValue)) : 0,
      dropCount: Math.max(0, previousValue - currentValue),
      comparableFromPrevious: true,
    };
  });
}

function frictionFromNode(node, maxTime = 1) {
  if (num(node.frictionScore) > 0) return safeRate(node.frictionScore);
  const drop = safeRate(node.dropRate);
  const timePressure = maxTime ? Math.min(30, (num(node.timeMs) / maxTime) * 30) : 0;
  const validation = Math.min(20, num(node.meta?.validationRate) * .2);
  const idle = Math.min(15, num(node.meta?.idleRatio) * .15);
  return Math.min(100, Math.round(drop * .55 + timePressure + validation + idle));
}

function buildJourney(analytics, acquisition) {
  const acqStages = acquisition?.stages || [];
  const activityFunnel = analytics?.funnel || [];

  const preDefs = [
    ["entry", "Visitantes", "entry"],
    ["intent", "Intención", "intent"],
    ["funnel", "Diagnóstico", "funnel"],
    ["contact", "Lead", "contact"],
    ["account", "Cuenta", "account"],
    ["project", "Negocio", "project"],
  ];
  const pre = preDefs.map(([sourceKey, label, key]) => {
    const row = findStage(acqStages, sourceKey);
    return {
      key: `journey:pre:${key}`,
      iconKey: key,
      sourceKey,
      label,
      value: num(row.visitors) || num(row.sessions),
      timeMs: num(row.medianFromPreviousMs),
      p90Ms: num(row.p90FromPreviousMs),
      frictionScore: num((acquisition?.friction || []).find((item) => item.stepKey === sourceKey)?.frictionScore),
      kind: "acquisition",
      cohort: "pre",
      cohortLabel: "Pre-registro instrumentado",
      meta: { sessions: row.sessions, visitors: row.visitors, samples: row.timeSampleSize },
    };
  });

  // BLOCK 4 · Activation journey mirrors the server's non-blocking core path.
  // STATUS: STATIC_PASS · STAGING_PENDING. Optional milestones (email, editor,
  // item) remain available in analytics.milestones and no longer falsify drop-off.
  const postDefs = [
    ["registeredAt", "Altas", "account_post"],
    ["firstLoginAt", "Panel", "panel"],
    ["firstActionAt", "Primera acción útil", "item"],
    ["firstVisitAt", "Web activa", "active"],
  ];
  const post = postDefs.map(([sourceKey, label, key]) => {
    const row = findStage(activityFunnel, sourceKey);
    return {
      key: `journey:post:${key}`,
      iconKey: key,
      sourceKey,
      label,
      value: num(row.count),
      timeMs: num(row.medianFromPreviousMs),
      p90Ms: num(row.p90FromPreviousMs),
      kind: "activation",
      cohort: "post",
      cohortLabel: "Altas del período",
      meta: { entered: row.entered, abandoned: row.abandoned, samples: row.timeSampleSize },
    };
  });

  return withComparableRates([...pre, ...post]);
}

function buildAcquisition(acquisition) {
  const rows = (acquisition?.stages || []).map((row) => ({
    key: `acq:${row.key}`,
    iconKey: row.key,
    sourceKey: row.key,
    label: row.label,
    value: num(row.visitors) || num(row.sessions),
    timeMs: num(row.medianFromPreviousMs),
    p90Ms: num(row.p90FromPreviousMs),
    frictionScore: num((acquisition?.friction || []).find((item) => item.stepKey === row.key)?.frictionScore),
    kind: "acquisition",
    cohort: "acquisition",
    cohortLabel: "Prospectos instrumentados",
    meta: { sessions: row.sessions, visitors: row.visitors, samples: row.timeSampleSize },
  }));
  return withComparableRates(rows);
}

function buildActivation(analytics) {
  return withComparableRates((analytics?.funnel || []).map((row) => ({
    key: `activation:${row.key}`,
    iconKey: row.key === "firstActionAt" ? "item" : row.key === "firstVisitAt" ? "active" : row.key === "firstLoginAt" ? "panel" : row.key === "projectCreatedAt" ? "project" : "default",
    sourceKey: row.key,
    label: row.label,
    value: num(row.count),
    timeMs: num(row.medianFromPreviousMs),
    p90Ms: num(row.p90FromPreviousMs),
    kind: "activation",
    cohort: "activation",
    cohortLabel: "Altas del período",
    meta: { entered: row.entered, abandoned: row.abandoned, samples: row.timeSampleSize },
  })));
}

const BILLING_STAGE_ORDER = [
  ["billing_conversion_cta_clicked", "Consultó continuidad"],
  ["billing_guide_opened", "Conoció los módulos"],
  ["billing_guide_starter_previewed", "Probó plan económico"],
  ["billing_guide_selection_applied", "Preparó selección"],
  ["billing_guide_selection_saved", "Guardó para después"],
  ["billing_checkout_intent", "Intentó activar"],
];

function aggregateBillingJourney(billing = {}) {
  const byAction = new Map((billing.stages || []).map((item) => [item.action, item]));
  return BILLING_STAGE_ORDER.map(([action, label]) => ({
    key: `billing:${action}`,
    sourceKey: action,
    label,
    value: num(byAction.get(action)?.projects),
    timeMs: 0,
    rate: 0,
    comparable: false,
    kind: "billing",
    cohort: "billing",
    cohortLabel: "Proyectos que hicieron la acción (sin atribución causal)",
    meta: { events: num(byAction.get(action)?.events), users: num(byAction.get(action)?.users) },
  }));
}

function BillingJourneyView({ billing = {} }) {
  const stages = (billing.stages || []);
  const byAction = new Map(stages.map((item) => [item.action, item]));
  const shown = BILLING_STAGE_ORDER.map(([action, label]) => ({ action, label, ...byAction.get(action) }));
  const max = Math.max(1, ...shown.map((item) => num(item.projects)));
  const sum = shown.reduce((total, row) => total + num(row.events), 0);
  if (!sum) return (
    <Box sx={{ p: 2.5, maxWidth: 470, mx: "auto", textAlign: "center" }}>
      <Typography variant="body1" sx={{ fontWeight: 950 }}>Todavía no hay acciones comerciales medidas</Typography>
      <Typography variant="body2" color="text.secondary">Cuando los clientes consulten sus módulos o preparen una suscripción, vas a ver aquí cuántos proyectos realizaron cada acción.</Typography>
    </Box>
  );
  return (
    <Box sx={{ width: "100%", maxWidth: 850, height: "100%", mx: "auto", p: { xs: 1, md: 2 }, overflowY: "auto" }}>
      <Typography variant="body1" sx={{ fontWeight: 950 }}>De conocer el producto a preparar el pago</Typography>
      <Typography variant="caption" color="text.secondary">Proyectos únicos por acción en el período · estas etapas no son una cohorte causal: no se calcula un porcentaje ficticio de conversión.</Typography>
      <Stack spacing={1.35} sx={{ mt: 2 }}>
        {shown.map((item, index) => (
          <Box key={item.action}>
            <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mb: .45 }}>
              <Chip size="small" label={index + 1} sx={{ height: 22, minWidth: 24, fontWeight: 900 }} />
              <Typography variant="body2" sx={{ flex: 1, minWidth: 0, fontWeight: 850 }}>{item.label}</Typography>
              <Typography variant="caption" sx={{ fontWeight: 950, whiteSpace: "nowrap" }}>{formatNumber(item.projects)} proyectos</Typography>
            </Stack>
            <Box sx={{ ml: 3.8, height: 8, bgcolor: "rgba(var(--wp-panel-ink-rgb,25,36,47),.07)", borderRadius: 99, overflow: "hidden" }}>
              <Box sx={{ width: `${num(item.projects) / max * 100}%`, height: "100%", bgcolor: BRAND.orange, borderRadius: 99 }} />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", ml: 3.8, mt: .25 }}>{formatNumber(item.events)} eventos · {formatNumber(item.users)} personas distintas</Typography>
          </Box>
        ))}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2.2 }}>Registrar una preferencia no cambia los módulos actuales ni inicia el cobro. Los pagos confirmados se computan por separado desde registros de facturación.</Typography>
    </Box>
  );
}

function BillingJourneyInspector({ billing = {} }) {
  const payments = billing.paymentEvidence || {};
  const sources = billing.sources || [];
  const interests = billing.moduleInterest || [];
  return (
    <Stack sx={{ height: "100%", overflowY: "auto" }} spacing={1.6}>
      <Typography variant="h6" sx={{ fontWeight: 950 }}>Lectura comercial</Typography>
      <Paper elevation={0} variant="outlined" sx={{ p: 1.5, borderRadius: 2.5 }}>
        <Typography variant="caption" color="text.secondary">PROYECTOS CON PAGO REGISTRADO EN EL PERÍODO</Typography>
        <Typography variant="h4" sx={{ fontWeight: 950 }}>{formatNumber(payments.projectsWithRecordedPayment)}</Typography>
        <Typography variant="caption" color="text.secondary">Evidencia desde el backend, no desde clics. No son necesariamente conversiones atribuibles a esta guía.</Typography>
      </Paper>
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 950 }}>Origen de interés en suscripción</Typography>
        {sources.slice(0, 8).map((row) => <Stack key={row.key} direction="row" justifyContent="space-between" spacing={1} sx={{ py: .4 }}><Typography variant="caption">{humanize(row.key)}</Typography><Typography variant="caption" sx={{ fontWeight: 900 }}>{formatNumber(row.count)}</Typography></Stack>)}
        {!sources.length ? <Typography variant="caption" color="text.secondary">Sin eventos todavía.</Typography> : null}
      </Box>
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 950 }}>Módulos explorados</Typography>
        {interests.slice(0, 7).map((row) => <Stack key={row.code} direction="row" justifyContent="space-between" spacing={1} sx={{ py: .4 }}><Typography variant="caption">{humanize(row.code)}</Typography><Typography variant="caption" sx={{ fontWeight: 900 }}>{formatNumber(row.events)}</Typography></Stack>)}
        {!interests.length ? <Typography variant="caption" color="text.secondary">Todavía no se modificaron combinaciones.</Typography> : null}
      </Box>
      {billing.coverage?.limited ? <Alert severity="warning">Se alcanzó el límite de muestra; evitá comparar tasas entre períodos sin ampliar la consulta.</Alert> : null}
    </Stack>
  );
}

function aggregateOnboardingSteps(onboarding = {}) {
  const grouped = new Map();
  (onboarding.steps || []).forEach((row) => {
    const key = row.stageKey || "unknown";
    if (!grouped.has(key)) {
      grouped.set(key, {
        key,
        sessions: 0,
        completedWeighted: 0,
        frictionWeighted: 0,
        blocked: 0,
        validationErrors: 0,
        formAbandonments: 0,
        medianActiveWeighted: 0,
        p90ActiveMs: 0,
        medianTargetWaitWeighted: 0,
        p90TargetWaitMs: 0,
        skipped: 0,
        deferred: 0,
      });
    }
    const current = grouped.get(key);
    const sessions = Math.max(1, num(row.sessions));
    current.sessions += num(row.sessions);
    current.completedWeighted += safeRate(row.completionRate) * sessions;
    current.frictionWeighted += num(row.frictionScore) * sessions;
    current.blocked += num(row.blocked);
    current.validationErrors += num(row.validationErrors);
    current.formAbandonments += num(row.formAbandonments);
    current.medianActiveWeighted += num(row.medianActiveMs) * sessions;
    current.p90ActiveMs = Math.max(current.p90ActiveMs, num(row.p90ActiveMs));
    current.medianTargetWaitWeighted += num(row.medianTargetWaitMs) * sessions;
    current.p90TargetWaitMs = Math.max(current.p90TargetWaitMs, num(row.p90TargetWaitMs));
    current.skipped += num(row.skipped);
    current.deferred += num(row.deferred);
  });

  return ONBOARDING_ORDER.map((key) => {
    const row = grouped.get(key) || { key };
    const denominator = Math.max(1, num(row.sessions));
    const completion = row.sessions ? row.completedWeighted / denominator : 0;
    return {
      key: `onboarding:${key}`,
      iconKey: key === "catalog" ? "item" : key === "first_sale" ? "active" : "default",
      sourceKey: key,
      label: ONBOARDING_LABELS[key] || humanize(key),
      value: num(row.sessions),
      rate: completion,
      dropRate: Math.max(0, 100 - completion),
      timeMs: row.sessions ? num(row.medianActiveWeighted) / denominator : 0,
      p90Ms: num(row.p90ActiveMs),
      frictionScore: row.sessions ? row.frictionWeighted / denominator : 0,
      kind: "onboarding",
      cohort: "onboarding",
      cohortLabel: "Tutorial",
      comparable: false,
      meta: {
        blocked: num(row.blocked),
        validationErrors: num(row.validationErrors),
        formAbandonments: num(row.formAbandonments),
        medianTargetWaitMs: row.sessions ? num(row.medianTargetWaitWeighted) / denominator : 0,
        p90TargetWaitMs: num(row.p90TargetWaitMs),
        skipped: num(row.skipped),
        deferred: num(row.deferred),
      },
    };
  });
}

function buildFriction(analytics, acquisition) {
  const rows = [];
  (acquisition?.friction || []).forEach((row) => rows.push({
    key: `friction:acq:${row.stepKey}`,
    sourceKey: row.stepKey,
    label: humanize(row.stepKey),
    value: num(row.frictionScore),
    frictionScore: num(row.frictionScore),
    dropRate: num(row.exitRate),
    timeMs: num(row.p90ActiveMs),
    kind: "friction",
    source: "Pre-registro",
    meta: { sessions: row.sessions, validationRate: row.validationRate, idleRatio: row.idleRatio, source: "Pre-registro" },
  }));
  (analytics?.screens || []).forEach((row) => rows.push({
    key: `friction:screen:${row.path}`,
    sourceKey: row.path,
    label: row.label || row.path,
    value: num(row.frictionScore),
    frictionScore: num(row.frictionScore),
    // V2 usa salida rápida como evidencia de fuga. endRate queda disponible en metadata
    // para compatibilidad y diagnóstico, pero ya no se interpreta automáticamente como fricción.
    dropRate: row.shortExitRate == null ? num(row.endRate) : num(row.shortExitRate),
    timeMs: num(row.p90DurationMs),
    kind: "friction",
    source: "Pantalla",
    meta: {
      sessions: row.sessions,
      users: row.uniqueUsers,
      repeatRate: row.repeatRate,
      longStayRate: row.longStayRate,
      endRate: row.endRate,
      shortExitRate: row.shortExitRate,
      confidence: row.frictionConfidence,
      frictionVersion: row.frictionVersion,
      source: "Pantalla",
    },
  }));
  (analytics?.operations || []).forEach((row) => rows.push({
    key: `friction:op:${row.key}`,
    sourceKey: row.key,
    label: row.label,
    value: num(row.frictionScore),
    frictionScore: num(row.frictionScore),
    dropRate: num(row.failRate),
    timeMs: num(row.p90DurationMs),
    kind: "friction",
    source: "Operación",
    meta: { sessions: row.uniqueUsers, uses: row.count, failures: row.failures, source: "Operación" },
  }));
  (analytics?.onboarding?.steps || []).forEach((row) => rows.push({
    key: `friction:onboarding:${row.key}`,
    sourceKey: row.key,
    label: ONBOARDING_LABELS[row.stageKey] || humanize(row.stageKey || row.tutorialKey || "Onboarding"),
    value: num(row.frictionScore),
    frictionScore: num(row.frictionScore),
    dropRate: Math.max(0, 100 - num(row.completionRate)),
    timeMs: num(row.p90ActiveMs),
    kind: "friction",
    source: "Onboarding",
    meta: { sessions: row.sessions, blocked: row.blocked, formAbandonments: row.formAbandonments, source: "Onboarding" },
  }));
  return rows
    .filter((row) => row.frictionScore > 0 || row.dropRate > 0)
    .sort((a, b) => b.frictionScore - a.frictionScore || b.dropRate - a.dropRate)
    .slice(0, 10);
}

function modeMetric(node, mode, maxTime) {
  if (mode === "time") return { value: num(node.timeMs), score: maxTime ? safeRate((num(node.timeMs) / maxTime) * 100) : 0, text: formatDuration(node.timeMs) };
  if (mode === "friction") {
    const friction = frictionFromNode(node, maxTime);
    return { value: friction, score: friction, text: `${formatNumber(friction)}/100` };
  }
  return { value: safeRate(node.rate), score: 100 - safeRate(node.rate), text: `${formatDecimal(node.rate)}%` };
}

function flowTone(node, mode, maxTime, isFirstInCohort = false) {
  if (mode === "time") return toneForRisk(modeMetric(node, mode, maxTime).score);
  if (mode === "friction") return toneForRisk(modeMetric(node, mode, maxTime).score);
  return isFirstInCohort ? BRAND.green : toneForHealth(node.rate);
}

function FlowChart({ rows, selectedKey, onSelect, mode }) {
  const width = Math.max(920, rows.length * 118 + 120);
  const height = 318;
  const baseY = 152;
  const left = 64;
  const right = width - 64;
  const spacing = rows.length > 1 ? (right - left) / (rows.length - 1) : 0;
  const cohortMax = {};
  rows.forEach((row) => {
    cohortMax[row.cohort] = Math.max(cohortMax[row.cohort] || 0, num(row.value));
  });
  const maxTime = Math.max(1, ...rows.map((row) => num(row.timeMs)));
  const positions = rows.map((row, index) => ({ ...row, x: left + index * spacing }));
  const thickness = (row) => {
    const maximum = Math.max(1, cohortMax[row.cohort] || 1);
    return 16 + 42 * Math.sqrt(Math.max(0, num(row.value)) / maximum);
  };

  return (
    <Box sx={{ width: "100%", height: "100%", minHeight: 250, display: "grid", placeItems: "center", overflow: "hidden" }}>
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" height="100%" preserveAspectRatio="xMidYMid meet" aria-label="Journey analítico de WebProfit">
        <defs>
          <filter id="wpJourneyGlow" x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>

        {positions.slice(1).map((node, index) => {
          const previous = positions[index];
          const sameCohort = previous.cohort === node.cohort;
          const comparable = sameCohort && node.comparable !== false && previous.comparable !== false;
          const mid = (previous.x + node.x) / 2;
          if (!sameCohort) {
            return (
              <g key={`boundary:${node.key}`}>
                <line x1={mid} x2={mid} y1="54" y2="252" stroke="rgba(245,245,245,.17)" strokeDasharray="5 7" />
                <text x={mid} y="38" textAnchor="middle" fill="rgba(245,245,245,.46)" fontSize="10" fontWeight="800">COHORTE</text>
                <text x={mid} y="270" textAnchor="middle" fill="rgba(245,245,245,.38)" fontSize="9">no se fuerza conversión</text>
              </g>
            );
          }
          if (!comparable) {
            return <line key={`rail:${node.key}`} x1={previous.x + 31} x2={node.x - 31} y1={baseY} y2={baseY} stroke="rgba(245,245,245,.15)" strokeWidth="3" strokeDasharray="6 7" />;
          }

          const t1 = thickness(previous);
          const t2 = thickness(node);
          const tone = flowTone(node, mode, maxTime, false);
          const path = [
            `M ${previous.x + 28} ${baseY - t1 / 2}`,
            `C ${mid - 22} ${baseY - t1 / 2}, ${mid + 22} ${baseY - t2 / 2}, ${node.x - 28} ${baseY - t2 / 2}`,
            `L ${node.x - 28} ${baseY + t2 / 2}`,
            `C ${mid + 22} ${baseY + t2 / 2}, ${mid - 22} ${baseY + t1 / 2}, ${previous.x + 28} ${baseY + t1 / 2}`,
            "Z",
          ].join(" ");
          const dropped = Math.max(0, num(previous.value) - num(node.value));
          const dropPct = num(previous.value) ? ratio(dropped, previous.value) : 0;
          return (
            <g key={`edge:${node.key}`}>
              <path d={path} fill={tone} fillOpacity=".22" stroke={tone} strokeOpacity=".16" />
              {dropped > 0 ? (
                <g>
                  <path
                    d={`M ${mid} ${baseY + Math.min(t1, t2) / 2} C ${mid} ${baseY + 46}, ${mid + 9} ${baseY + 70}, ${mid + 9} ${baseY + 94}`}
                    fill="none"
                    stroke={dropPct >= 35 ? BRAND.red : BRAND.amber}
                    strokeOpacity=".68"
                    strokeWidth={Math.max(2, Math.min(8, 2 + dropPct / 12))}
                    strokeLinecap="round"
                  />
                  <circle cx={mid + 9} cy={baseY + 98} r="3.5" fill={dropPct >= 35 ? BRAND.red : BRAND.amber} />
                  <text x={mid + 18} y={baseY + 102} fill={dropPct >= 35 ? BRAND.red : BRAND.amber} fontSize="11" fontWeight="900">-{formatNumber(dropped)}</text>
                </g>
              ) : null}
            </g>
          );
        })}

        {positions.map((node, index) => {
          const selected = node.key === selectedKey;
          const previous = index > 0 ? positions[index - 1] : null;
          const firstInCohort = !previous || previous.cohort !== node.cohort;
          const tone = selected ? BRAND.orange : flowTone(node, mode, maxTime, firstInCohort);
          const metric = modeMetric(node, mode, maxTime);
          const radius = 27;
          return (
            <g
              key={node.key}
              role="button"
              tabIndex="0"
              onClick={() => onSelect(node.key)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") onSelect(node.key);
              }}
              style={{ cursor: "pointer", outline: "none" }}
            >
              <title>{`${node.label}: ${formatNumber(node.value)} · ${metric.text}`}</title>
              {firstInCohort ? (
                <text x={node.x} y="58" textAnchor="middle" fill="rgba(245,245,245,.44)" fontSize="9" fontWeight="900">{node.cohortLabel?.toUpperCase()}</text>
              ) : null}
              {selected ? <circle cx={node.x} cy={baseY} r={radius + 9} fill={BRAND.orange} fillOpacity=".10" filter="url(#wpJourneyGlow)" /> : null}
              <circle cx={node.x} cy={baseY} r={radius} fill="#1B1C20" stroke={tone} strokeWidth={selected ? 3 : 2} />
              <foreignObject x={node.x - 10} y={baseY - 10} width="20" height="20" style={{ color: tone, pointerEvents: "none" }}>
                <div xmlns="http://www.w3.org/1999/xhtml" style={{ display: "grid", placeItems: "center", width: "20px", height: "20px" }}>
                  {ICONS[node.iconKey] || ICONS.default}
                </div>
              </foreignObject>
              <text x={node.x} y={baseY - 43} textAnchor="middle" fill="rgba(245,245,245,.90)" fontSize="11" fontWeight="900">{node.label.length > 17 ? `${node.label.slice(0, 16)}…` : node.label}</text>
              <text x={node.x} y={baseY + 48} textAnchor="middle" fill="#F5F5F5" fontSize="22" fontWeight="950">{formatNumber(node.value)}</text>
              <text x={node.x} y={baseY + 65} textAnchor="middle" fill={tone} fontSize="10.5" fontWeight="900">{metric.text}</text>
            </g>
          );
        })}
      </svg>
    </Box>
  );
}

function FrictionLandscape({ rows, selectedKey, onSelect }) {
  const max = Math.max(1, ...rows.map((row) => num(row.frictionScore)));
  return (
    <Box sx={{ width: "100%", height: "100%", minHeight: 250, display: "flex", alignItems: "stretch", gap: 1.1, px: .5, py: 2 }}>
      {rows.map((row) => {
        const score = safeRate(row.frictionScore);
        const selected = row.key === selectedKey;
        const height = Math.max(12, (score / max) * 100);
        const tone = toneForRisk(score);
        return (
          <Box
            component="button"
            key={row.key}
            type="button"
            onClick={() => onSelect(row.key)}
            sx={{
              appearance: "none",
              border: 0,
              bgcolor: "transparent",
              color: "inherit",
              minWidth: 0,
              flex: 1,
              p: 0,
              cursor: "pointer",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
              alignItems: "stretch",
              gap: .65,
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 900, color: selected ? BRAND.orange : "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.label}</Typography>
            <Box sx={{ flex: 1, minHeight: 120, display: "flex", alignItems: "flex-end", justifyContent: "center", position: "relative" }}>
              <Box sx={{ width: "72%", height: `${height}%`, maxHeight: "100%", minHeight: 10, borderRadius: "12px 12px 4px 4px", bgcolor: tone, opacity: selected ? 1 : .72, boxShadow: selected ? `0 0 0 2px ${BRAND.orange},0 10px 26px rgba(0,0,0,.22)` : "none", transition: "height .2s ease,opacity .2s ease" }} />
              <Typography sx={{ position: "absolute", bottom: 7, fontSize: 13, fontWeight: 950, color: "#fff" }}>{formatNumber(score)}</Typography>
            </Box>
            <Typography variant="caption" sx={{ color: "text.secondary", fontSize: 10 }}>{row.source}</Typography>
          </Box>
        );
      })}
    </Box>
  );
}

function MetricBox({ label, value, tone, helper }) {
  return (
    <Box sx={{ p: 1, borderRadius: 2.1, border: `1px solid ${BRAND.border}`, bgcolor: "rgba(var(--wp-panel-ink-rgb,245,245,245),.018)", minWidth: 0 }}>
      <Typography variant="caption" sx={{ display: "block", color: "text.secondary", fontWeight: 850, fontSize: 9.5 }}>{label}</Typography>
      <Typography sx={{ mt: .2, fontSize: 17, lineHeight: 1.1, fontWeight: 950, color: tone || "text.primary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value}</Typography>
      {helper ? <Typography variant="caption" sx={{ display: "block", mt: .25, color: "text.secondary", fontSize: 9.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{helper}</Typography> : null}
    </Box>
  );
}

function RingMetric({ value, label, inverse = false }) {
  const safe = safeRate(value);
  const tone = inverse ? toneForRisk(safe) : toneForHealth(safe);
  return (
    <Box sx={{ width: 88, height: 88, borderRadius: "50%", p: "7px", background: `conic-gradient(${tone} ${safe * 3.6}deg, rgba(var(--wp-panel-ink-rgb,245,245,245),.08) 0deg)`, flex: "0 0 auto" }}>
      <Box sx={{ width: "100%", height: "100%", borderRadius: "50%", bgcolor: BRAND.surface, display: "grid", placeItems: "center", textAlign: "center", px: .5 }}>
        <Box>
          <Typography sx={{ fontSize: 18, lineHeight: 1, fontWeight: 950 }}>{formatDecimal(safe)}%</Typography>
          <Typography variant="caption" sx={{ color: "text.secondary", fontSize: 8.5, fontWeight: 850 }}>{label}</Typography>
        </Box>
      </Box>
    </Box>
  );
}

function MiniBarList({ title, rows = [], valueKey = "count", labelKey = "label", maxRows = 5, tone = BRAND.orange, suffix = "" }) {
  const visible = rows.slice(0, maxRows);
  const maxValue = Math.max(1, ...visible.map((row) => Math.abs(num(row[valueKey]))));
  if (!visible.length) return null;
  return (
    <Box>
      <Typography variant="caption" sx={{ fontWeight: 950, color: "text.secondary", fontSize: 9.5 }}>{title}</Typography>
      <Stack spacing={.65} sx={{ mt: .65 }}>
        {visible.map((row, index) => {
          const value = num(row[valueKey]);
          return (
            <Box key={`${row[labelKey]}:${index}`}>
              <Stack direction="row" justifyContent="space-between" spacing={1}>
                <Typography variant="caption" sx={{ color: "text.primary", fontWeight: 780, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>{row[labelKey]}</Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 900, flex: "0 0 auto" }}>{value > 0 && suffix === "pp" ? "+" : ""}{formatDecimal(value)}{suffix}</Typography>
              </Stack>
              <Box sx={{ mt: .32, height: 4, borderRadius: 99, bgcolor: BRAND.faint, overflow: "hidden" }}>
                <Box sx={{ height: "100%", width: `${Math.max(4, (Math.abs(value) / maxValue) * 100)}%`, bgcolor: typeof tone === "function" ? tone(row) : tone, borderRadius: 99 }} />
              </Box>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}

function topFactorSignals(acquisition) {
  const dimensions = acquisition?.factors?.dimensions || [];
  const rows = dimensions.flatMap((dimension) => (dimension.rows || []).map((row) => ({
    ...row,
    dimensionKey: dimension.key,
    dimensionLabel: dimension.label,
    label: `${dimension.label}: ${humanize(row.key)}`,
  })));
  return rows
    .filter((row) => row.key !== "unknown" && row.sessions >= 2 && num(row.signalStrength) > 0)
    .sort((a, b) => num(b.signalStrength) - num(a.signalStrength) || b.sessions - a.sessions)
    .slice(0, 6);
}

function acquisitionFormForNode(node, acquisition) {
  const forms = acquisition?.forms || [];
  if (["contact", "registration", "account"].includes(node?.sourceKey)) return forms.find((row) => row.formKey === "contact") || null;
  if (["business", "system", "volume", "need", "plan"].includes(node?.sourceKey)) return forms.find((row) => row.formKey === "business_identity") || null;
  return null;
}

function workflowForNode(node, analytics) {
  const workflows = analytics?.onboarding?.workflows || [];
  const key = node?.sourceKey;
  if (["firstItemAt", "catalog"].includes(key)) return workflows.find((row) => row.key === "item_entry") || workflows.find((row) => row.key === "import") || null;
  if (key === "initial_stock") return workflows.find((row) => row.key === "stock_count") || null;
  return null;
}

function operationForNode(node, analytics) {
  const operations = analytics?.operations || [];
  const map = {
    projectCreatedAt: "project_created",
    firstEditAt: "landing_editor_save_completed",
    firstItemAt: "product_created",
  };
  const wanted = map[node?.sourceKey];
  if (!wanted) return null;
  return operations.find((row) => row.action === wanted || row.key === wanted || String(row.key || "").startsWith(`${wanted}:`)) || null;
}

function Inspector({ node, lens, mode, analytics, acquisition }) {
  if (!node) return null;
  const maxTime = Math.max(1, num(node.timeMs), num(node.p90Ms));
  const friction = frictionFromNode(node, maxTime);
  const conversion = safeRate(node.rate);
  const form = acquisitionFormForNode(node, acquisition);
  const workflow = workflowForNode(node, analytics);
  const operation = operationForNode(node, analytics);
  const factors = topFactorSignals(acquisition);
  const factorRows = factors.map((row) => ({
    label: row.label,
    value: Math.abs(num(row.projectLift) || num(row.accountLift)),
    signed: num(row.projectLift) || num(row.accountLift),
    confidence: row.confidence,
    sessions: row.sessions,
  }));
  const invalidFields = (analytics?.onboarding?.forms || [])
    .flatMap((row) => row.topInvalidFields || [])
    .reduce((acc, item) => {
      const current = acc.find((row) => row.label === humanize(item.fieldKey));
      if (current) current.count += num(item.count);
      else acc.push({ label: humanize(item.fieldKey), count: num(item.count) });
      return acc;
    }, [])
    .sort((a, b) => b.count - a.count);

  const abandonmentFields = (form?.abandonmentFields || []).map((row) => ({ label: humanize(row.key), count: row.count }));
  const reasonRows = (workflow?.topReasons || []).map((row) => ({ label: humanize(row.reasonCode), count: row.count }));
  const itemFormSteps = ["firstItemAt", "catalog"].includes(node?.sourceKey)
    ? (analytics?.onboarding?.itemFormSteps || [])
    : [];

  let reading = "Etapa estable para el volumen observado.";
  if (friction >= 65) reading = "Fricción alta: conviene revisar esta etapa antes de sumar más complejidad.";
  else if (safeRate(node.dropRate) >= 35) reading = "La pérdida está concentrada en esta transición.";
  else if (num(node.timeMs) > 2 * 60 * 1000) reading = "La conversión puede ser aceptable, pero la tarea está consumiendo demasiado tiempo humano.";

  return (
    <Box sx={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 950, fontSize: 9.5 }}>{node.meta?.source || node.cohortLabel || humanize(lens)}</Typography>
          <Typography sx={{ mt: .15, fontSize: 20, lineHeight: 1.05, fontWeight: 950, overflowWrap: "anywhere" }}>{node.label}</Typography>
        </Box>
        <Chip size="small" label={mode === "time" ? "TIEMPO" : mode === "friction" || lens === "friction" ? "FRICCIÓN" : "CONVERSIÓN"} sx={{ height: 22, fontSize: 9, fontWeight: 950, bgcolor: "rgba(243,90,58,.10)", color: BRAND.orange }} />
      </Stack>

      <Stack direction="row" spacing={1.1} alignItems="center" sx={{ mt: 1.25, pb: 1.25, borderBottom: `1px solid ${BRAND.border}` }}>
        <RingMetric value={lens === "friction" ? friction : conversion} label={lens === "friction" ? "fricción" : "convierte"} inverse={lens === "friction"} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 34, lineHeight: .95, fontWeight: 950, letterSpacing: "-.04em" }}>{formatNumber(node.value)}</Typography>
          <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 800 }}>personas / sesiones observadas</Typography>
          <Typography variant="body2" sx={{ mt: .7, color: "text.secondary", lineHeight: 1.35 }}>{reading}</Typography>
        </Box>
      </Stack>

      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: .8, mt: 1 }}>
        <MetricBox label="CONVERSIÓN" value={`${formatDecimal(conversion)}%`} tone={toneForHealth(conversion)} />
        <MetricBox label="FUGA" value={`${formatDecimal(node.dropRate)}%`} tone={toneForRisk(node.dropRate)} />
        <MetricBox label="P50 / PASO" value={formatDuration(node.timeMs)} />
        <MetricBox label="P90" value={formatDuration(node.p90Ms || node.timeMs)} />
      </Box>

      <Box sx={{ mt: 1.1, minHeight: 0, flex: 1, overflowY: "auto", pr: .25, scrollbarWidth: "thin" }}>
        <Stack spacing={1.25}>
          {reasonRows.length ? <MiniBarList title="MOTIVOS DEL WORKFLOW" rows={reasonRows} valueKey="count" labelKey="label" tone={BRAND.red} /> : null}
          {abandonmentFields.length ? <MiniBarList title="DÓNDE DEJAN EL FORMULARIO" rows={abandonmentFields} valueKey="count" labelKey="label" tone={BRAND.amber} /> : null}
          {!abandonmentFields.length && invalidFields.length && ["firstItemAt", "catalog", "initial_stock"].includes(node.sourceKey) ? <MiniBarList title="CAMPOS CON MÁS VALIDACIONES" rows={invalidFields} valueKey="count" labelKey="label" tone={BRAND.amber} /> : null}
          {node.kind === "onboarding" ? (
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 950, color: "text.secondary", fontSize: 9.5 }}>COMPORTAMIENTO DE LA GUÍA</Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: .65, mt: .65 }}>
                <MetricBox label="HASTA LA ACCIÓN" value={formatDuration(node.meta?.medianTargetWaitMs)} />
                <MetricBox label="P90 ACCIÓN" value={formatDuration(node.meta?.p90TargetWaitMs)} />
                <MetricBox label="BLOQUEOS" value={formatNumber(node.meta?.blocked)} tone={num(node.meta?.blocked) ? BRAND.amber : undefined} />
                <MetricBox label="OMITE / DIFIERE" value={formatNumber(num(node.meta?.skipped) + num(node.meta?.deferred))} />
              </Box>
            </Box>
          ) : null}
          {itemFormSteps.length ? (
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 950, color: "text.secondary", fontSize: 9.5 }}>FORMULARIO DE ÍTEMS · POR SECCIÓN</Typography>
              <Stack spacing={.55} sx={{ mt: .65 }}>
                {itemFormSteps.map((row) => (
                  <Stack key={row.key} direction="row" alignItems="center" spacing={.75} sx={{ px: .75, py: .6, borderRadius: 1.7, bgcolor: "rgba(var(--wp-panel-ink-rgb,25,36,47),.025)" }}>
                    <Typography sx={{ minWidth: 0, flex: 1, fontSize: 11.2, fontWeight: 850 }} noWrap>{row.stepIndex + 1}. {row.stepKey}</Typography>
                    <Typography sx={{ fontSize: 10.5, fontWeight: 950, color: safeRate(row.completionRate) < 65 ? BRAND.amber : "text.secondary", whiteSpace: "nowrap" }}>{formatDecimal(row.completionRate)}%</Typography>
                    <Typography sx={{ width: 52, textAlign: "right", fontSize: 10.2, color: "text.secondary", whiteSpace: "nowrap" }}>{formatDuration(row.medianActiveMs)}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
          ) : null}
          {workflow ? (
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 950, color: "text.secondary", fontSize: 9.5 }}>TRABAJO HUMANO</Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: .65, mt: .65 }}>
                <MetricBox label="COMPLETA" value={`${formatDecimal(workflow.completionRate)}%`} />
                <MetricBox label="FALLA / DEJA" value={`${formatDecimal(workflow.failureRate)}%`} />
                <MetricBox label="P50" value={formatDuration(workflow.medianDurationMs)} />
                <MetricBox label="P90" value={formatDuration(workflow.p90DurationMs)} />
              </Box>
            </Box>
          ) : null}
          {operation ? (
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 950, color: "text.secondary", fontSize: 9.5 }}>OPERACIÓN REAL</Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: .65, mt: .65 }}>
                <MetricBox label="USOS" value={formatNumber(operation.count)} />
                <MetricBox label="FALLA" value={`${formatDecimal(operation.failRate)}%`} tone={toneForRisk(operation.failRate)} />
              </Box>
            </Box>
          ) : null}
          {factorRows.length && ["journey", "acquisition"].includes(lens) ? (
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 950, color: "text.secondary", fontSize: 9.5 }}>FACTORES ASOCIADOS · NO CAUSALES</Typography>
              <Stack spacing={.72} sx={{ mt: .7 }}>
                {factorRows.slice(0, 4).map((row) => {
                  const signed = num(row.signed);
                  const positive = signed >= 0;
                  return (
                    <Box key={row.label} sx={{ p: .8, border: `1px solid ${BRAND.border}`, borderRadius: 2 }}>
                      <Stack direction="row" justifyContent="space-between" spacing={1}>
                        <Typography variant="caption" sx={{ fontWeight: 850, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.label}</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 950, color: positive ? BRAND.green : BRAND.red, flex: "0 0 auto" }}>{positive ? "+" : ""}{formatDecimal(signed)} pp</Typography>
                      </Stack>
                      <Typography variant="caption" sx={{ color: "text.secondary", fontSize: 9.5 }}>{formatNumber(row.sessions)} sesiones · confianza {row.confidence === "high" ? "alta" : row.confidence === "medium" ? "media" : "baja"}</Typography>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          ) : null}
        </Stack>
      </Box>
    </Box>
  );
}

function InsightRibbon({ analytics, acquisition, lensRows, lens }) {
  const biggestFlowDrop = [...lensRows]
    .filter((row) => row.comparableFromPrevious)
    .sort((a, b) => num(b.dropRate) - num(a.dropRate))[0];
  const worstFriction = buildFriction(analytics, acquisition)[0];
  const topFactor = topFactorSignals(acquisition)[0];
  const items = [];

  if (biggestFlowDrop && lens !== "friction") items.push({ icon: <RouteRoundedIcon sx={{ fontSize: 15 }} />, label: "Mayor fuga", value: `${biggestFlowDrop.label} · ${formatDecimal(biggestFlowDrop.dropRate)}%`, tone: toneForRisk(biggestFlowDrop.dropRate) });
  if (worstFriction) items.push({ icon: <WarningAmberRoundedIcon sx={{ fontSize: 15 }} />, label: "Mayor fricción", value: `${worstFriction.label} · ${formatNumber(worstFriction.frictionScore)}/100`, tone: toneForRisk(worstFriction.frictionScore) });
  if (topFactor) {
    const lift = num(topFactor.projectLift) || num(topFactor.accountLift);
    items.push({ icon: <QueryStatsRoundedIcon sx={{ fontSize: 15 }} />, label: "Factor observado", value: `${topFactor.dimensionLabel}: ${humanize(topFactor.key)} ${lift >= 0 ? "+" : ""}${formatDecimal(lift)} pp`, tone: lift >= 0 ? BRAND.green : BRAND.red });
  }

  return (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minHeight: 28, overflowX: "auto", scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}>
      {items.slice(0, 3).map((item) => (
        <Stack key={`${item.label}:${item.value}`} direction="row" spacing={.55} alignItems="center" sx={{ minWidth: "max-content" }}>
          <Box sx={{ color: item.tone, display: "grid", placeItems: "center" }}>{item.icon}</Box>
          <Typography variant="caption" sx={{ fontWeight: 900 }}>{item.label}</Typography>
          <Typography variant="caption" sx={{ color: item.tone, fontWeight: 850 }}>{item.value}</Typography>
        </Stack>
      ))}
    </Stack>
  );
}

function buildGptAnalyticsExport({ analytics, acquisition, days, project }) {
  const onboarding = analytics?.onboarding || {};
  return {
    schema: "webprofit.analytics.gpt.v1",
    generatedAt: new Date().toISOString(),
    periodDays: Number(days || 0),
    projectFilter: project || "all",
    privacy: "Aggregates plus de-identified structural samples. Prospect/session PII intentionally excluded.",
    activation: {
      overview: analytics?.overview || {},
      funnel: analytics?.funnel || [],
      screens: analytics?.screens || [],
      operations: analytics?.operations || [],
    },
    billingJourney: analytics?.billingJourney || {},
    onboarding: {
      coverage: onboarding.coverage || {},
      overview: onboarding.overview || {},
      steps: onboarding.steps || [],
      forms: onboarding.forms || [],
      screens: onboarding.screens || [],
      workflows: onboarding.workflows || [],
      // Muestras estructurales sin identidad para que GPT pueda detectar patrones
      // de pausa, abandono y recuperación que se pierden en un promedio global.
      sessionSamples: (onboarding.sessions || []).slice(0, 80).map((row) => ({
        tutorialKey: row.tutorialKey || "",
        stageKey: row.stageKey || "",
        stepIndex: Number(row.stepIndex || 0),
        completed: Boolean(row.completedAt),
        exitedOrPaused: Boolean(row.exitedAt),
        screenActiveMs: Number(row.screenActiveMs || 0),
        screenIdleMs: Number(row.screenIdleMs || 0),
        screenHiddenMs: Number(row.screenHiddenMs || 0),
        idleRate: Number(row.idleRate || 0),
        validationErrors: Number(row.validationErrors || 0),
        formAbandonments: Number(row.formAbandonments || 0),
        blockedSteps: Number(row.blockedSteps || 0),
        submitAttempts: Number(row.submitAttempts || 0),
        screens: Number(row.screens || 0),
        events: Number(row.events || 0),
      })),
    },
    acquisition: {
      coverage: acquisition?.coverage || {},
      overview: acquisition?.overview || {},
      stages: acquisition?.stages || [],
      friction: acquisition?.friction || [],
      forms: acquisition?.forms || [],
      factors: acquisition?.factors || {},
      sources: acquisition?.sources || acquisition?.channels || [],
      prospectSamples: (acquisition?.recent || []).slice(0, 60).map((row) => ({
        status: row.status || "",
        currentStep: row.currentStep || "",
        completionPct: Number(row.completionPct || 0),
        businessProfile: row.businessProfile || "",
        currentSystem: row.currentSystem || "",
        itemVolumeBand: row.itemVolumeBand || "",
        primaryNeed: row.primaryNeed || "",
        source: row.source || "",
        campaign: row.campaign || "",
        medium: row.medium || "",
        deviceType: row.deviceType || "",
        activeMs: Number(row.activeMs || 0),
        idleMs: Number(row.idleMs || 0),
        backtracks: Number(row.backtracks || 0),
        validationErrors: Number(row.validationErrors || 0),
        resumes: Number(row.resumes || 0),
        exits: Number(row.exits || 0),
        sessionsForVisitor: Number(row.sessionsForVisitor || 1),
        isReturning: Boolean(row.isReturning),
        hasAccount: Boolean(row.linkedUserId),
        hasProject: Boolean(row.linkedProjectId),
      })),
    },
  };
}


function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("es-AR", { dateStyle: "short", timeStyle: "short" }).format(date);
}

const LEAD_STATUS_OPTIONS = [
  { value: "", label: "Todos los estados" },
  { value: "contact_captured", label: "Lead" },
  { value: "registration_started", label: "Registro iniciado" },
  { value: "account_created", label: "Cuenta creada" },
  { value: "project_created", label: "Negocio creado" },
];

function leadStatusMeta(status) {
  const map = {
    contact_captured: { label: "Lead", color: "warning" },
    registration_started: { label: "Registro", color: "info" },
    account_created: { label: "Cuenta", color: "success" },
    project_created: { label: "Proyecto", color: "success" },
    qualified: { label: "Interés", color: "default" },
    in_progress: { label: "En curso", color: "default" },
    started: { label: "Visita", color: "default" },
  };
  return map[status] || { label: humanize(status), color: "default" };
}

function whatsappHref(value) {
  const digits = String(value || "").replace(/\D+/g, "");
  return digits.length >= 6 ? `https://wa.me/${digits}` : "";
}

function LeadCrm({ state, search, onSearch, status, onStatus, onPage }) {
  const items = state.items || [];
  const pagination = state.pagination || { page: 1, pages: 1, total: 0 };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${BRAND.border}`,
        bgcolor: BRAND.surface,
        color: "var(--wp-panel-text-primary, #19242F)",
        "& .MuiTableCell-root": { color: "var(--wp-panel-text-primary, #19242F)" },
        overflow: "hidden",
        flex: "0 0 auto",
      }}
    >
      <Stack direction={{ xs: "column", md: "row" }} spacing={1} justifyContent="space-between" alignItems={{ md: "center" }} sx={{ px: 1.25, py: 1 }}>
        <Box>
          <Stack direction="row" spacing={.65} alignItems="center">
            <PersonAddAlt1RoundedIcon sx={{ fontSize: 19, color: BRAND.orange }} />
            <Typography sx={{ fontWeight: 950, fontSize: 17 }}>Leads Web Profit</Typography>
            <Chip size="small" label={`${formatNumber(pagination.total)} contactos`} sx={{ height: 22, fontWeight: 900 }} />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            CRM de adquisición: datos que la persona dejó antes de registrarse, con su origen, interés y avance real.
          </Typography>
        </Box>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={.65} sx={{ width: { xs: "100%", md: "auto" } }}>
          <TextField
            size="small"
            value={search}
            onChange={(event) => onSearch(event.target.value)}
            placeholder="Buscar nombre, negocio, email, WhatsApp o campaña"
            sx={{ minWidth: { md: 330 }, "& .MuiOutlinedInput-root": { borderRadius: 99, height: 36 } }}
          />
          <FormControl size="small" sx={{ minWidth: 165 }}>
            <InputLabel>Estado</InputLabel>
            <Select value={status} label="Estado" onChange={(event) => onStatus(event.target.value)} sx={{ borderRadius: 99, height: 36, fontWeight: 850 }}>
              {LEAD_STATUS_OPTIONS.map((option) => <MenuItem key={option.value || "all"} value={option.value}>{option.label}</MenuItem>)}
            </Select>
          </FormControl>
        </Stack>
      </Stack>

      {state.error ? <Alert severity="warning" sx={{ mx: 1.25, mb: 1 }}>{state.error}</Alert> : null}

      <TableContainer sx={{ maxHeight: 430, borderTop: `1px solid ${BRAND.border}` }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 950 }}>Lead</TableCell>
              <TableCell sx={{ fontWeight: 950 }}>Negocio</TableCell>
              <TableCell sx={{ fontWeight: 950 }}>Contacto</TableCell>
              <TableCell sx={{ fontWeight: 950 }}>Interés</TableCell>
              <TableCell sx={{ fontWeight: 950 }}>Origen</TableCell>
              <TableCell sx={{ fontWeight: 950 }}>Estado</TableCell>
              <TableCell align="right" sx={{ fontWeight: 950 }}>Última actividad</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {state.loading && !items.length ? (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}><WebProfitSpinner size={24} /></TableCell></TableRow>
            ) : items.length ? items.map((row) => {
              const statusMeta = leadStatusMeta(row.status);
              const webAnswer = typeof row.profile?.wantsWebsite === "boolean" ? row.profile.wantsWebsite : null;
              const stockAnswer = typeof row.profile?.wantsStock === "boolean" ? row.profile.wantsStock : null;
              const waHref = whatsappHref(row.contact?.whatsapp);
              return (
                <TableRow key={row.sessionId || row._id} hover>
                  <TableCell sx={{ minWidth: 170 }}>
                    <Typography sx={{ fontWeight: 900, fontSize: 13 }}>{row.contact?.name || "Sin nombre"}</Typography>
                    <Typography variant="caption" color="text.secondary">{row.contact?.email || "Sin email"}</Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 150 }}>
                    <Typography sx={{ fontWeight: 850, fontSize: 13 }}>{row.profile?.businessName || "Sin nombre"}</Typography>
                    {row.profile?.businessProfile ? <Typography variant="caption" color="text.secondary">{humanize(row.profile.businessProfile)}</Typography> : null}
                  </TableCell>
                  <TableCell sx={{ minWidth: 180 }}>
                    <Stack direction="row" spacing={.45} flexWrap="wrap" useFlexGap>
                      {waHref ? <Button component="a" href={waHref} target="_blank" rel="noreferrer" size="small" variant="outlined" sx={{ minWidth: 0, px: .8, py: .15, borderRadius: 99, textTransform: "none", fontSize: 11, fontWeight: 900 }}>WhatsApp</Button> : null}
                      {row.contact?.email ? <Button component="a" href={`mailto:${row.contact.email}`} size="small" variant="outlined" sx={{ minWidth: 0, px: .8, py: .15, borderRadius: 99, textTransform: "none", fontSize: 11, fontWeight: 900 }}>Email</Button> : null}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: .3 }}>{row.contact?.whatsapp || "Sin WhatsApp"}</Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 180 }}>
                    <Stack direction="row" spacing={.4} flexWrap="wrap" useFlexGap>
                      {row.profile?.primaryNeed ? <Chip size="small" label={primaryNeedLabel(row.profile.primaryNeed)} sx={{ height: 21, fontSize: 10.5, fontWeight: 900 }} /> : null}
                      <Chip size="small" variant="outlined" label={`Web ${webAnswer === null ? "—" : webAnswer ? "Sí" : "No"}`} sx={{ height: 21, fontSize: 10.5, fontWeight: 850 }} />
                      <Chip size="small" variant="outlined" label={`Stock ${stockAnswer === null ? "—" : stockAnswer ? "Sí" : "No"}`} sx={{ height: 21, fontSize: 10.5, fontWeight: 850 }} />
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ minWidth: 150 }}>
                    <Typography sx={{ fontWeight: 850, fontSize: 12.5 }}>{row.campaign?.source || row.campaign?.refererHost || "Directo"}</Typography>
                    <Typography variant="caption" color="text.secondary">{row.campaign?.campaign || row.campaign?.medium || "Sin campaña"}</Typography>
                  </TableCell>
                  <TableCell><Chip size="small" color={statusMeta.color} label={statusMeta.label} sx={{ height: 23, fontWeight: 900 }} /></TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    <Typography sx={{ fontWeight: 800, fontSize: 12 }}>{formatDateTime(row.metrics?.lastSeenAt || row.updatedAt || row.createdAt)}</Typography>
                    <Typography variant="caption" color="text.secondary">{formatNumber(row.completionPct)}% funnel</Typography>
                  </TableCell>
                </TableRow>
              );
            }) : (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: "text.secondary" }}>No hay leads para este filtro.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 1.25, py: .75, borderTop: `1px solid ${BRAND.border}` }}>
        <Typography variant="caption" color="text.secondary">Página {pagination.page || 1} de {pagination.pages || 1}</Typography>
        <Stack direction="row" spacing={.55}>
          <Button size="small" variant="outlined" disabled={state.loading || (pagination.page || 1) <= 1} onClick={() => onPage((pagination.page || 1) - 1)} sx={{ borderRadius: 99, textTransform: "none", fontWeight: 900 }}>Anterior</Button>
          <Button size="small" variant="outlined" disabled={state.loading || (pagination.page || 1) >= (pagination.pages || 1)} onClick={() => onPage((pagination.page || 1) + 1)} sx={{ borderRadius: 99, textTransform: "none", fontWeight: 900 }}>Siguiente</Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

function clientRouteLabel(path = "") {
  const clean = String(path || "").split("?")[0].split("#")[0];
  const rules = [
    [/\/pagina(?:\/|$)/i, "Editor de la web"],
    [/\/productos(?:\/|$)/i, "Productos y servicios"],
    [/\/sistema\/items(?:\/|$)/i, "Ítems"],
    [/\/sistema\/movimientos(?:\/|$)/i, "Movimientos"],
    [/\/sistema\/caja(?:\/|$)/i, "Caja"],
    [/\/sistema\/stock(?:\/|$)/i, "Stock"],
    [/\/clientes(?:\/|$)/i, "Clientes"],
    [/\/pedidos(?:\/|$)/i, "Pedidos"],
    [/\/agenda(?:\/|$)/i, "Agenda"],
    [/\/estadisticas(?:\/|$)/i, "Estadísticas"],
    [/\/configuracion(?:\/|$)/i, "Configuración"],
    [/\/inicio(?:\/|$)/i, "Inicio"],
  ];
  return rules.find(([pattern]) => pattern.test(clean))?.[1] || clean || "Panel";
}

function shortDay(value) {
  const date = new Date(`${value}T12:00:00Z`);
  if (Number.isNaN(date.getTime())) return value || "—";
  return new Intl.DateTimeFormat("es-AR", { weekday: "short", timeZone: "UTC" }).format(date).replace(/\.$/, "");
}

function shortCalendarDate(value) {
  const date = new Date(`${value}T12:00:00Z`);
  if (Number.isNaN(date.getTime())) return value || "—";
  return new Intl.DateTimeFormat("es-AR", { day: "2-digit", month: "2-digit", timeZone: "UTC" }).format(date);
}

function actorOneWord(actor = {}) {
  const name = String(actor?.name || "").trim();
  if (name) return name.split(/\s+/)[0];
  const email = String(actor?.email || "").trim();
  if (email) return email.split("@")[0].split(/[._-]+/)[0] || "Usuario";
  return "Usuario";
}

function signedNumber(value) {
  const number = num(value);
  return `${number > 0 ? "+" : ""}${formatNumber(number)}`;
}

function escapeHtml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function printClientUsageReport(data) {
  if (!data?.usageReport || typeof window === "undefined") return;
  const report = data.usageReport;
  const summary = report.summary || {};
  const tenant = report.tenant || {};
  const owner = data.owner || {};
  const modules = (data.observedUsage || []).filter((row) => row.observed === true);
  const navRows = (report.recentNavigation || []).slice(0, 12);
  const operationRows = (report.recentOperations || []).slice(0, 12);
  const itemRows = (tenant.items?.latest || []).slice(0, 10);
  const stockRows = (tenant.stock?.latestMovements || []).slice(0, 10);

  const tableRows = (rows, render) => rows.length
    ? rows.map(render).join("")
    : '<tr><td colspan="4" class="empty">Sin actividad registrada.</td></tr>';
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(data.name || "Cliente")} · Uso Web Profit</title><style>
    body{font-family:Arial,sans-serif;color:#17181c;margin:28px;font-size:12px}h1{font-size:24px;margin:0 0 4px}h2{font-size:15px;margin:22px 0 8px;border-bottom:1px solid #ddd;padding-bottom:5px}.muted{color:#666}.grid{display:grid;grid-template-columns:repeat(4,1fr);gap:8px;margin:16px 0}.kpi{border:1px solid #ddd;border-radius:9px;padding:9px}.kpi b{font-size:18px;display:block;margin-top:3px}table{width:100%;border-collapse:collapse;margin:5px 0 14px}th,td{text-align:left;border-bottom:1px solid #e8e8e8;padding:6px 5px;vertical-align:top}th{font-size:10px;text-transform:uppercase;color:#666}.empty{text-align:center;color:#777;padding:15px}.pill{display:inline-block;border:1px solid #ccc;border-radius:99px;padding:3px 7px;margin:2px}.footer{margin-top:24px;border-top:1px solid #ddd;padding-top:8px;color:#777;font-size:10px}@media print{body{margin:12mm}.no-print{display:none}}
  </style></head><body>
    <h1>${escapeHtml(data.name || data.slug || "Cliente")}</h1>
    <div class="muted">Reporte de uso real de Web Profit · ${escapeHtml(report.period?.label || data.range?.label || "Período seleccionado")}</div>
    <div class="muted">Responsable: ${escapeHtml(owner.name || "Sin nombre")} · ${escapeHtml(owner.email || "Sin email")} · Última actividad: ${escapeHtml(formatDateTime(summary.lastSeenAt))}</div>
    <div class="grid">
      <div class="kpi">Días activos esta semana<b>${escapeHtml(summary.activeDaysThisWeek || 0)}/7</b></div>
      <div class="kpi">Navegaciones esta semana<b>${escapeHtml(formatNumber(summary.navigationsThisWeek))}</b></div>
      <div class="kpi">Tiempo registrado esta semana<b>${escapeHtml(formatDuration(summary.navigationDurationMsThisWeek))}</b></div>
      <div class="kpi">Operaciones esta semana<b>${escapeHtml(formatNumber(summary.operationsThisWeek))}</b></div>
      <div class="kpi">Ítems actuales<b>${escapeHtml(formatNumber(tenant.items?.total))}</b></div>
      <div class="kpi">Ítems creados en período<b>${escapeHtml(formatNumber(tenant.items?.createdInPeriod))}</b></div>
      <div class="kpi">Stock actual<b>${escapeHtml(formatNumber(tenant.stock?.currentUnits))}</b></div>
      <div class="kpi">Movimientos de stock<b>${escapeHtml(formatNumber(tenant.stock?.movementsInPeriod))}</b></div>
    </div>
    <h2>Módulos observados</h2><div>${modules.length ? modules.map((row) => `<span class="pill">${escapeHtml(row.label)} · ${escapeHtml(formatNumber(row.eventCount))}</span>`).join("") : '<span class="muted">Sin uso instrumentado en el período.</span>'}</div>
    <h2>Últimas navegaciones</h2><table><thead><tr><th>Fecha</th><th>Pantalla</th><th>Usuario</th><th>Tiempo</th></tr></thead><tbody>${tableRows(navRows, (row) => `<tr><td>${escapeHtml(formatDateTime(row.occurredAt))}</td><td>${escapeHtml(clientRouteLabel(row.path))}<br><span class="muted">${escapeHtml(row.path)}</span></td><td>${escapeHtml(actorOneWord(row.actor))}</td><td>${escapeHtml(formatDuration(row.durationMs))}</td></tr>`)}</tbody></table>
    <h2>Qué estuvo haciendo</h2><table><thead><tr><th>Fecha</th><th>Acción</th><th>Detalle</th><th>Usuario</th></tr></thead><tbody>${tableRows(operationRows, (row) => `<tr><td>${escapeHtml(formatDateTime(row.occurredAt))}</td><td>${escapeHtml(row.title || humanize(row.action))}</td><td>${escapeHtml(row.description || "")}</td><td>${escapeHtml(actorOneWord(row.actor))}</td></tr>`)}</tbody></table>
    <h2>Últimos ítems cargados</h2><table><thead><tr><th>Fecha</th><th>Ítem</th><th>Categoría</th><th>Stock</th></tr></thead><tbody>${tableRows(itemRows, (row) => `<tr><td>${escapeHtml(formatDateTime(row.createdAt))}</td><td>${escapeHtml(row.name)}</td><td>${escapeHtml(row.category || humanize(row.kind))}</td><td>${escapeHtml(formatNumber(row.stock))}</td></tr>`)}</tbody></table>
    <h2>Últimos movimientos de stock</h2><table><thead><tr><th>Fecha</th><th>Ítem</th><th>Movimiento</th><th>Stock</th></tr></thead><tbody>${tableRows(stockRows, (row) => `<tr><td>${escapeHtml(formatDateTime(row.occurredAt))}</td><td>${escapeHtml(row.productName)}</td><td>${escapeHtml(humanize(row.kind))} · ${escapeHtml(signedNumber(row.quantityDelta))}</td><td>${escapeHtml(formatNumber(row.stockBefore))} → ${escapeHtml(formatNumber(row.stockAfter))}</td></tr>`)}</tbody></table>
    <div class="footer">Generado desde Superadmin · ${escapeHtml(new Intl.DateTimeFormat("es-AR", { dateStyle: "full", timeStyle: "short" }).format(new Date()))}<br>${escapeHtml(report.note || "")}</div>
  </body></html>`;
  const popup = window.open("", "_blank", "width=1100,height=800");
  if (!popup) return;
  try { popup.opener = null; } catch (_error) {}
  popup.document.open();
  popup.document.write(html);
  popup.document.close();
  popup.focus();
  window.setTimeout(() => popup.print(), 250);
}

function UsageMetric({ label, value, helper }) {
  return (
    <Box sx={{ minWidth: 0, p: .85, borderRadius: 2.25, border: `1px solid ${BRAND.border}`, bgcolor: "rgba(var(--wp-panel-ink-rgb,245,245,245),.018)" }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontWeight: 800 }}>{label}</Typography>
      <Typography sx={{ mt: .1, fontWeight: 950, fontSize: 18, lineHeight: 1.05 }}>{value}</Typography>
      {helper ? <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: .25 }}>{helper}</Typography> : null}
    </Box>
  );
}

function ClientUsageReport({ projects, project, onProject, user, onUser, days, state, onReload }) {
  const data = state.data;
  const report = data?.usageReport || null;
  const summary = report?.summary || {};
  const tenant = report?.tenant || {};
  const week = report?.week?.days || [];
  const usageUsers = data?.usageUsers || [];
  const selectedUsageUser = usageUsers.find((item) => item.id === user) || null;
  const maxWeekEvents = Math.max(1, ...week.map((row) => num(row.events)));

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${BRAND.border}`,
        bgcolor: BRAND.surface,
        color: "var(--wp-panel-text-primary, #19242F)",
        "& .MuiTableCell-root": { color: "var(--wp-panel-text-primary, #19242F)" },
        overflow: "hidden",
        flex: "0 0 auto",
      }}
    >
      <Stack direction={{ xs: "column", lg: "row" }} spacing={1} justifyContent="space-between" alignItems={{ lg: "center" }} sx={{ px: 1.25, py: 1 }}>
        <Box>
          <Stack direction="row" spacing={.65} alignItems="center">
            <HistoryRoundedIcon sx={{ fontSize: 19, color: BRAND.green }} />
            <Typography sx={{ fontWeight: 950, fontSize: 17 }}>Uso real por cliente</Typography>
            {data?.name ? <Chip size="small" label={data.name} sx={{ height: 22, fontWeight: 900 }} /> : null}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Actividad operativa auditable: navegación, trabajo realizado, ítems y stock. Período: {days} días{selectedUsageUser ? ` · Usuario: ${actorOneWord(selectedUsageUser)}` : " · Todos los usuarios"}.
          </Typography>
        </Box>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={.65} sx={{ minWidth: { lg: 650 } }}>
          <FormControl size="small" fullWidth>
            <InputLabel>Cliente / proyecto</InputLabel>
            <Select value={project} label="Cliente / proyecto" onChange={(event) => onProject(event.target.value)} sx={{ borderRadius: 99, height: 36, fontWeight: 850 }}>
              <MenuItem value="">Seleccionar cliente</MenuItem>
              {[...projects].sort((a, b) => String(a.name || a.slug || "").localeCompare(String(b.name || b.slug || ""), "es")).map((item) => {
                const value = item.slug || item.id || item._id;
                return <MenuItem key={value} value={value}>{item.name || item.slug}</MenuItem>;
              })}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth disabled={!project || state.loading}>
            <InputLabel>Usuario</InputLabel>
            <Select value={user} label="Usuario" onChange={(event) => onUser(event.target.value)} sx={{ borderRadius: 99, height: 36, fontWeight: 850 }}>
              <MenuItem value="">Todos</MenuItem>
              {usageUsers.map((item) => <MenuItem key={item.id} value={item.id}>{item.name || item.email || "Usuario"}</MenuItem>)}
            </Select>
          </FormControl>
          <Button size="small" variant="outlined" onClick={onReload} disabled={!project || state.loading} sx={{ height: 36, minWidth: 36, width: 36, p: 0, borderRadius: 99 }}>
            {state.loading ? <WebProfitSpinner size={15} /> : <RefreshRoundedIcon fontSize="small" />}
          </Button>
          <Button size="small" variant="contained" disableElevation startIcon={<PrintRoundedIcon fontSize="small" />} onClick={() => printClientUsageReport(data)} disabled={!report} sx={{ height: 36, borderRadius: 99, px: 1.3, whiteSpace: "nowrap", textTransform: "none", fontWeight: 900, bgcolor: BRAND.orange, "&:hover": { bgcolor: BRAND.orange } }}>
            Imprimir informe
          </Button>
        </Stack>
      </Stack>

      {state.error ? <Alert severity="warning" sx={{ mx: 1.25, mb: 1 }}>{state.error}</Alert> : null}
      {!project ? (
        <Box sx={{ px: 2, py: 4, textAlign: "center", borderTop: `1px solid ${BRAND.border}` }}>
          <BusinessRoundedIcon sx={{ fontSize: 36, opacity: .35 }} />
          <Typography sx={{ mt: .5, fontWeight: 900 }}>Elegí un cliente para ver su uso real.</Typography>
          <Typography variant="caption" color="text.secondary">No se mezclan proyectos: la lectura baja a la actividad y a la base operativa del tenant seleccionado.</Typography>
        </Box>
      ) : state.loading && !data ? (
        <Box sx={{ py: 5, textAlign: "center", borderTop: `1px solid ${BRAND.border}` }}><WebProfitSpinner size={28} /></Box>
      ) : report ? (
        <Box sx={{ px: 1.25, pb: 1.25, borderTop: `1px solid ${BRAND.border}` }}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2,minmax(0,1fr))", md: "repeat(4,minmax(0,1fr))", xl: "repeat(8,minmax(0,1fr))" }, gap: .6, py: .85 }}>
            <UsageMetric label="Días activos · 7d" value={`${formatNumber(summary.activeDaysThisWeek)}/7`} helper={`${formatNumber(summary.eventsThisWeek)} eventos`} />
            <UsageMetric label="Navegaciones · 7d" value={formatNumber(summary.navigationsThisWeek)} helper={`${formatNumber(summary.sessions)} sesiones período`} />
            <UsageMetric label="Tiempo panel · 7d" value={formatDuration(summary.navigationDurationMsThisWeek)} helper="tiempo registrado" />
            <UsageMetric label="Operaciones · 7d" value={formatNumber(summary.operationsThisWeek)} helper={`${formatNumber(summary.operations)} en período`} />
            <UsageMetric label="Ítems actuales" value={formatNumber(tenant.items?.total)} helper={`+${formatNumber(tenant.items?.createdInPeriod)} en período`} />
            <UsageMetric label="Stock actual" value={formatNumber(tenant.stock?.currentUnits)} helper={`${formatNumber(tenant.stock?.trackedItems)} ítems controlados`} />
            <UsageMetric label="Mov. stock" value={formatNumber(tenant.stock?.movementsInPeriod)} helper={`${signedNumber(tenant.stock?.netDeltaInPeriod)} neto`} />
            <UsageMetric label="Última actividad" value={summary.lastSeenAt ? formatDateTime(summary.lastSeenAt).split(",")[0] : "—"} helper={summary.lastSeenAt ? formatDateTime(summary.lastSeenAt) : "Sin registro"} />
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", xl: "minmax(0,1.25fr) minmax(0,.75fr)" }, gap: .7 }}>
            <Box sx={{ minWidth: 0, border: `1px solid ${BRAND.border}`, borderRadius: 2.5, p: .8 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: .6 }}>
                <Typography sx={{ fontWeight: 950, fontSize: 13 }}>Uso durante los últimos 7 días</Typography>
                <Typography variant="caption" color="text.secondary">{selectedUsageUser ? actorOneWord(selectedUsageUser) : "Todos"} · altura = eventos</Typography>
              </Stack>
              <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${Math.max(week.length, 1)}, minmax(0,1fr))`, gap: .45, height: 86, alignItems: "end" }}>
                {week.length ? week.map((row) => (
                  <Stack key={row.date} spacing={.25} alignItems="center" justifyContent="flex-end" sx={{ height: "100%", minWidth: 0 }}>
                    <Typography sx={{ fontSize: 9.5, fontWeight: 900 }}>{formatNumber(row.events)}</Typography>
                    <Box sx={{ width: "68%", minHeight: 3, height: `${Math.max(4, Math.round((num(row.events) / maxWeekEvents) * 48))}px`, borderRadius: "5px 5px 2px 2px", bgcolor: num(row.events) ? BRAND.green : BRAND.faint }} />
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9, lineHeight: 1 }}>{shortDay(row.date)}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 8.5, lineHeight: 1 }}>{shortCalendarDate(row.date)}</Typography>
                  </Stack>
                )) : <Typography variant="caption" color="text.secondary">Sin actividad esta semana.</Typography>}
              </Box>
            </Box>

            <Box sx={{ minWidth: 0, border: `1px solid ${BRAND.border}`, borderRadius: 2.5, p: .8 }}>
              <Typography sx={{ fontWeight: 950, fontSize: 13, mb: .55 }}>Módulos que realmente usó</Typography>
              <Stack direction="row" spacing={.4} flexWrap="wrap" useFlexGap>
                {(data.observedUsage || []).filter((row) => row.observed === true).length
                  ? (data.observedUsage || []).filter((row) => row.observed === true).map((row) => <Chip key={row.key} size="small" variant="outlined" label={`${row.label} · ${formatNumber(row.eventCount)}`} sx={{ height: 22, fontSize: 10.5, fontWeight: 850 }} />)
                  : <Typography variant="caption" color="text.secondary">No hay módulos con uso instrumentado en el período.</Typography>}
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: .6 }}>{report.note}</Typography>
            </Box>
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "repeat(2,minmax(0,1fr))" }, gap: .7, mt: .7 }}>
            <Box sx={{ minWidth: 0, border: `1px solid ${BRAND.border}`, borderRadius: 2.5, overflow: "hidden" }}>
              <Typography sx={{ px: .85, py: .65, fontWeight: 950, fontSize: 13, borderBottom: `1px solid ${BRAND.border}` }}>Últimas navegaciones</Typography>
              <TableContainer sx={{ maxHeight: 235 }}><Table size="small" stickyHeader><TableHead><TableRow><TableCell>Pantalla</TableCell><TableCell>Usuario</TableCell><TableCell>Fecha</TableCell><TableCell align="right">Tiempo</TableCell></TableRow></TableHead><TableBody>
                {(report.recentNavigation || []).length ? report.recentNavigation.map((row) => <TableRow key={row.id} hover><TableCell><Typography sx={{ fontSize: 12, fontWeight: 900 }}>{clientRouteLabel(row.path)}</Typography></TableCell><TableCell><Typography sx={{ fontSize: 11.5 }}>{actorOneWord(row.actor)}</Typography></TableCell><TableCell sx={{ whiteSpace: "nowrap", fontSize: 11.5 }}>{formatDateTime(row.occurredAt)}</TableCell><TableCell align="right" sx={{ whiteSpace: "nowrap", fontWeight: 850 }}>{formatDuration(row.durationMs)}</TableCell></TableRow>) : <TableRow><TableCell colSpan={4} align="center" sx={{ py: 3, color: "text.secondary" }}>Sin navegaciones registradas.</TableCell></TableRow>}
              </TableBody></Table></TableContainer>
            </Box>

            <Box sx={{ minWidth: 0, border: `1px solid ${BRAND.border}`, borderRadius: 2.5, overflow: "hidden" }}>
              <Typography sx={{ px: .85, py: .65, fontWeight: 950, fontSize: 13, borderBottom: `1px solid ${BRAND.border}` }}>Qué estuvo haciendo</Typography>
              <TableContainer sx={{ maxHeight: 235 }}><Table size="small" stickyHeader><TableHead><TableRow><TableCell>Acción</TableCell><TableCell>Usuario</TableCell><TableCell align="right">Fecha</TableCell></TableRow></TableHead><TableBody>
                {(report.recentOperations || []).length ? report.recentOperations.map((row) => <TableRow key={row.id} hover><TableCell><Typography sx={{ fontSize: 12, fontWeight: 900 }}>{row.title || humanize(row.action)}</Typography><Typography variant="caption" color="text.secondary">{row.description || humanize(row.category)}</Typography></TableCell><TableCell><Typography sx={{ fontSize: 11.5 }}>{actorOneWord(row.actor)}</Typography></TableCell><TableCell align="right" sx={{ whiteSpace: "nowrap", fontSize: 11.5 }}>{formatDateTime(row.occurredAt)}</TableCell></TableRow>) : <TableRow><TableCell colSpan={3} align="center" sx={{ py: 3, color: "text.secondary" }}>Sin operaciones registradas.</TableCell></TableRow>}
              </TableBody></Table></TableContainer>
            </Box>

            <Box sx={{ minWidth: 0, border: `1px solid ${BRAND.border}`, borderRadius: 2.5, overflow: "hidden" }}>
              <Typography sx={{ px: .85, py: .65, fontWeight: 950, fontSize: 13, borderBottom: `1px solid ${BRAND.border}` }}>Últimos ítems cargados</Typography>
              <TableContainer sx={{ maxHeight: 235 }}><Table size="small" stickyHeader><TableHead><TableRow><TableCell>Ítem</TableCell><TableCell>Categoría</TableCell><TableCell align="right">Stock</TableCell></TableRow></TableHead><TableBody>
                {(tenant.items?.latest || []).length ? tenant.items.latest.map((row) => <TableRow key={row.id} hover><TableCell><Typography sx={{ fontSize: 12, fontWeight: 900 }}>{row.name}</Typography><Typography variant="caption" color="text.secondary">{formatDateTime(row.createdAt)}</Typography></TableCell><TableCell sx={{ fontSize: 11.5 }}>{row.category || humanize(row.kind)}</TableCell><TableCell align="right" sx={{ fontWeight: 900 }}>{formatNumber(row.stock)}</TableCell></TableRow>) : <TableRow><TableCell colSpan={3} align="center" sx={{ py: 3, color: "text.secondary" }}>Sin ítems cargados.</TableCell></TableRow>}
              </TableBody></Table></TableContainer>
            </Box>

            <Box sx={{ minWidth: 0, border: `1px solid ${BRAND.border}`, borderRadius: 2.5, overflow: "hidden" }}>
              <Typography sx={{ px: .85, py: .65, fontWeight: 950, fontSize: 13, borderBottom: `1px solid ${BRAND.border}` }}>Últimos movimientos de stock</Typography>
              <TableContainer sx={{ maxHeight: 235 }}><Table size="small" stickyHeader><TableHead><TableRow><TableCell>Ítem</TableCell><TableCell>Movimiento</TableCell><TableCell align="right">Stock</TableCell></TableRow></TableHead><TableBody>
                {(tenant.stock?.latestMovements || []).length ? tenant.stock.latestMovements.map((row) => <TableRow key={row.id} hover><TableCell><Typography sx={{ fontSize: 12, fontWeight: 900 }}>{row.productName}</Typography><Typography variant="caption" color="text.secondary">{formatDateTime(row.occurredAt)}</Typography></TableCell><TableCell><Chip size="small" variant="outlined" label={`${humanize(row.kind)} · ${signedNumber(row.quantityDelta)}`} sx={{ height: 21, fontSize: 10.5, fontWeight: 850 }} /></TableCell><TableCell align="right" sx={{ whiteSpace: "nowrap", fontSize: 11.5 }}>{formatNumber(row.stockBefore)} → <b>{formatNumber(row.stockAfter)}</b></TableCell></TableRow>) : <TableRow><TableCell colSpan={3} align="center" sx={{ py: 3, color: "text.secondary" }}>Sin movimientos de stock.</TableCell></TableRow>}
              </TableBody></Table></TableContainer>
            </Box>
          </Box>
        </Box>
      ) : null}
    </Paper>
  );
}

function downloadJsonFile(filename, payload) {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

export default function SuperAnalyticsPage() {
  const { projects, loadProjects } = useProjects();
  const [days, setDays] = React.useState(30);
  const [project, setProject] = React.useState("");
  const [lens, setLens] = React.useState("journey");
  const [mode, setMode] = React.useState("conversion");
  const [selectedKey, setSelectedKey] = React.useState("");
  const [analytics, setAnalytics] = React.useState(null);
  const [acquisition, setAcquisition] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [leadSearch, setLeadSearch] = React.useState("");
  const [leadStatus, setLeadStatus] = React.useState("");
  const [leadPage, setLeadPage] = React.useState(1);
  const [leadState, setLeadState] = React.useState({ loading: true, error: "", items: [], pagination: { page: 1, pages: 1, total: 0 } });
  const [clientUsageState, setClientUsageState] = React.useState({ loading: false, error: "", data: null });
  const [usageUser, setUsageUser] = React.useState("");

  React.useEffect(() => {
    if (!projects.length) loadProjects();
  }, [loadProjects, projects.length]);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError("");
    const [activityResult, acquisitionResult] = await Promise.allSettled([
      adminApi.activityAnalytics({ days, ...(project ? { project } : {}) }),
      acquisitionApi.adminAnalytics({ hours: days * 24 }),
    ]);

    if (activityResult.status === "fulfilled") setAnalytics(activityResult.value?.analytics || null);
    if (acquisitionResult.status === "fulfilled") setAcquisition(acquisitionResult.value || null);

    const errors = [];
    if (activityResult.status === "rejected") errors.push(activityResult.reason?.message || "activación");
    if (acquisitionResult.status === "rejected") errors.push(acquisitionResult.reason?.message || "adquisición");
    if (errors.length) setError(`Datos parciales: ${errors.join(" · ")}`);
    setLoading(false);
  }, [days, project]);

  React.useEffect(() => {
    load();
  }, [load]);

  const loadLeads = React.useCallback(async () => {
    setLeadState((current) => ({ ...current, loading: true, error: "" }));
    try {
      const data = await acquisitionApi.adminProspects({
        page: leadPage,
        limit: 75,
        leadOnly: true,
        ...(leadSearch.trim() ? { search: leadSearch.trim() } : {}),
        ...(leadStatus ? { status: leadStatus } : {}),
      });
      setLeadState({
        loading: false,
        error: "",
        items: data?.items || [],
        pagination: data?.pagination || { page: leadPage, pages: 1, total: (data?.items || []).length },
      });
    } catch (leadError) {
      setLeadState((current) => ({ ...current, loading: false, error: leadError?.message || "No se pudo cargar el CRM de leads." }));
    }
  }, [leadPage, leadSearch, leadStatus]);

  React.useEffect(() => {
    // SUPERADMIN LEAD CRM · Small debounce keeps server-side search responsive
    // without firing one request per keystroke. STATUS: STATIC_PASS · RUNTIME_PENDING.
    const timer = window.setTimeout(loadLeads, 250);
    return () => window.clearTimeout(timer);
  }, [loadLeads]);

  // CLIENT USAGE FILTER · V2
  // STATUS: STATIC_PASS · RUNTIME_PENDING. userId is server-side, so charts/tables are not cosmetically filtered.
  const loadClientUsage = React.useCallback(async () => {
    if (!project) {
      setClientUsageState({ loading: false, error: "", data: null });
      return;
    }
    setClientUsageState((current) => ({ ...current, loading: true, error: "" }));
    try {
      const response = await adminApi.commandCenterProject(project, { days, ...(usageUser ? { userId: usageUser } : {}) });
      setClientUsageState({ loading: false, error: "", data: response?.project || null });
    } catch (usageError) {
      setClientUsageState((current) => ({ ...current, loading: false, error: usageError?.message || "No se pudo cargar el uso real del cliente." }));
    }
  }, [days, project, usageUser]);

  React.useEffect(() => {
    // SUPERADMIN CLIENT USAGE · Detailed tenant reads happen only after an
    // explicit client selection. STATUS: STATIC_PASS · RUNTIME_PENDING.
    loadClientUsage();
  }, [loadClientUsage]);

  const lensRows = React.useMemo(() => {
    if (lens === "acquisition") return buildAcquisition(acquisition);
    if (lens === "activation") return buildActivation(analytics);
    if (lens === "onboarding") return aggregateOnboardingSteps(analytics?.onboarding || {});
    if (lens === "billing") return aggregateBillingJourney(analytics?.billingJourney || {});
    if (lens === "friction") return buildFriction(analytics, acquisition);
    return buildJourney(analytics, acquisition);
  }, [acquisition, analytics, lens]);

  React.useEffect(() => {
    if (!lensRows.length) {
      setSelectedKey("");
      return;
    }
    if (!lensRows.some((row) => row.key === selectedKey)) {
      const defaultNode = lens === "friction"
        ? lensRows[0]
        : [...lensRows].filter((row) => row.comparableFromPrevious).sort((a, b) => num(b.dropRate) - num(a.dropRate))[0] || lensRows[0];
      setSelectedKey(defaultNode?.key || "");
    }
  }, [lens, lensRows, selectedKey]);

  React.useEffect(() => {
    if (lens === "friction") setMode("friction");
    else if (mode === "friction" && lens === "onboarding") setMode("conversion");
  }, [lens, mode]);

  const selected = lensRows.find((row) => row.key === selectedKey) || lensRows[0] || null;
  const projectName = project ? (projects.find((item) => (item.slug || item.id || item._id) === project)?.name || project) : "Todos";
  const overview = analytics?.overview || {};
  const acqOverview = acquisition?.overview || {};

  const exportForGpt = React.useCallback(() => {
    const payload = buildGptAnalyticsExport({ analytics, acquisition, days, project });
    const date = new Date().toISOString().slice(0, 10);
    downloadJsonFile(`webprofit-analytics-${date}-${days}d.json`, payload);
  }, [acquisition, analytics, days, project]);

  return (
    <Box
      sx={{
        minHeight: "calc(100dvh - 180px)",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        gap: .85,
      }}
    >
      <Paper elevation={0} sx={{ px: 1, py: .7, borderRadius: 3, border: `1px solid ${BRAND.border}`, bgcolor: BRAND.surface, flex: "0 0 auto" }}>
        <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={.28} sx={{ minWidth: 0, overflowX: "auto", scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}>
            {LENSES.map((item) => (
              <Button
                key={item.key}
                size="small"
                onClick={() => setLens(item.key)}
                variant={lens === item.key ? "contained" : "text"}
                disableElevation
                sx={{ borderRadius: 99, minWidth: "auto", px: 1.2, py: .55, textTransform: "none", fontWeight: 900, color: lens === item.key ? "#fff" : "text.secondary", bgcolor: lens === item.key ? BRAND.orange : "transparent", whiteSpace: "nowrap", "&:hover": { bgcolor: lens === item.key ? BRAND.orange : "rgba(243,90,58,.07)" } }}
              >
                {item.label}
              </Button>
            ))}
          </Stack>

          <Stack direction="row" spacing={.55} alignItems="center" sx={{ flex: "0 0 auto" }}>
            <FormControl size="small" sx={{ minWidth: 122, display: { xs: "none", xl: "block" } }}>
              <InputLabel>Proyecto</InputLabel>
              <Select value={project} label="Proyecto" onChange={(event) => setProject(event.target.value)} sx={{ borderRadius: 99, fontWeight: 850, height: 34 }}>
                <MenuItem value="">Todos</MenuItem>
                {[...projects].sort((a, b) => String(a.name || a.slug || "").localeCompare(String(b.name || b.slug || ""), "es")).map((item) => {
                  const value = item.slug || item.id || item._id;
                  return <MenuItem key={value} value={value}>{item.name || item.slug}</MenuItem>;
                })}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Período</InputLabel>
              <Select value={days} label="Período" onChange={(event) => setDays(Number(event.target.value))} sx={{ borderRadius: 99, fontWeight: 850, height: 34 }}>
                {PERIODS.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
              </Select>
            </FormControl>
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadRoundedIcon fontSize="small" />}
              onClick={exportForGpt}
              disabled={!analytics && !acquisition}
              sx={{ height: 34, borderRadius: 99, px: 1.1, textTransform: "none", fontWeight: 900, display: { xs: "none", md: "inline-flex" } }}
            >
              Exportar GPT
            </Button>
            <Button size="small" variant="outlined" onClick={() => { load(); loadLeads(); }} disabled={loading} sx={{ minWidth: 34, width: 34, height: 34, p: 0, borderRadius: 99 }}>
              {loading ? <WebProfitSpinner size={15} /> : <RefreshRoundedIcon fontSize="small" />}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {lens === "billing" && analytics?.billingJourney ? (
        <Paper variant="outlined" sx={{ px: 1.5, py: 1, borderRadius: 3, flex: "0 0 auto", bgcolor: BRAND.surface, borderColor: BRAND.border }}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "center" }} spacing={1}>
            <Stack direction="row" alignItems="center" flexWrap="wrap" gap={0.75}>
              <Chip size="small" label={`${formatNumber(analytics.billingJourney.paymentEvidence?.projectsWithRecordedPayment)} proyectos con pagos registrados`} color="success" />
              <Chip size="small" label={`${formatNumber(analytics.billingJourney.paymentEvidence?.approvedPeriodPayments)} pagos por período aprobados`} />
              <Chip size="small" label={`${formatNumber(analytics.billingJourney.paymentEvidence?.recurringRecordsWithApprovedPayment)} registros recurrentes pagados`} />
            </Stack>
            <Typography variant="caption" color="text.secondary">El pago lo confirma el backend. Los clics de la guía son intención, no cobros.</Typography>
          </Stack>
          {analytics.billingJourney.coverage?.limited ? <Typography variant="caption" color="warning.main">Muestra de eventos limitada: no comparar porcentajes como tasas globales.</Typography> : null}
        </Paper>
      ) : null}
      <Paper
        elevation={0}
        sx={{
          flex: "0 0 auto",
          height: "calc(100dvh - 230px)",
          minHeight: 520,
          borderRadius: 3,
          border: `1px solid ${BRAND.border}`,
          bgcolor: BRAND.surface,
          overflow: "hidden",
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0,1fr) 330px", xl: "minmax(0,1fr) 360px" },
        }}
      >
        <Box sx={{ minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column", p: { xs: 1, md: 1.25 } }}>
          <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center" sx={{ flex: "0 0 auto" }}>
            <Stack direction="row" spacing={.5} alignItems="center" sx={{ minWidth: 0, overflowX: "auto", scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}>
              <Chip size="small" variant="outlined" label={projectName} sx={{ height: 23, fontWeight: 850 }} />
              <Chip size="small" variant="outlined" label={`${days}d`} sx={{ height: 23, fontWeight: 850 }} />
              {lens === "journey" ? <Chip size="small" label={`${formatNumber(acqOverview.visitors)} visitantes`} sx={{ height: 23, fontWeight: 850 }} /> : null}
              {lens === "journey" ? <Chip size="small" label={`${formatNumber(overview.registrations)} altas`} sx={{ height: 23, fontWeight: 850 }} /> : null}
              {lens === "onboarding" ? <Chip size="small" variant="outlined" label={`${formatNumber(analytics?.onboarding?.overview?.helpInteractions?.siteOpens)} entradas a Mi web`} sx={{ height: 23, fontWeight: 850 }} /> : null}
              {lens === "onboarding" ? <Chip size="small" variant="outlined" label={`${formatNumber(analytics?.onboarding?.overview?.helpInteractions?.movementsGuideOpens)} guías de movimientos`} sx={{ height: 23, fontWeight: 850 }} /> : null}
              {lens === "onboarding" ? <Chip size="small" variant="outlined" label={`${formatNumber(analytics?.onboarding?.overview?.helpInteractions?.historicalChoicesReviewed)} consultas de inicio histórico`} sx={{ height: 23, fontWeight: 850 }} /> : null}
              {lens === "onboarding" ? <Chip size="small" variant="outlined" label={`${formatNumber(analytics?.onboarding?.overview?.helpInteractions?.cashFeePreviewOpens)} simulaciones de caja`} sx={{ height: 23, fontWeight: 850 }} /> : null}
              {num(acquisition?.coverage?.authenticatedExcluded) > 0 && lens === "acquisition" ? <Chip size="small" variant="outlined" label={`${formatNumber(acquisition.coverage.authenticatedExcluded)} pruebas excluidas`} sx={{ height: 23, fontWeight: 850 }} /> : null}
            </Stack>

            {lens !== "friction" ? (
              <Stack direction="row" spacing={.2} sx={{ p: .25, borderRadius: 99, bgcolor: "rgba(var(--wp-panel-ink-rgb,245,245,245),.04)", flex: "0 0 auto" }}>
                {MODES.map((item) => (
                  <Button
                    key={item.key}
                    size="small"
                    onClick={() => setMode(item.key)}
                    startIcon={item.icon}
                    sx={{ minWidth: "auto", px: .9, py: .35, borderRadius: 99, textTransform: "none", fontSize: 11, fontWeight: 900, color: mode === item.key ? "#fff" : "text.secondary", bgcolor: mode === item.key ? "rgba(243,90,58,.88)" : "transparent", "& .MuiButton-startIcon": { mr: .35 }, "&:hover": { bgcolor: mode === item.key ? BRAND.orange : "rgba(243,90,58,.07)" } }}
                  >
                    {item.label}
                  </Button>
                ))}
              </Stack>
            ) : null}
          </Stack>

          {error ? <Alert severity="warning" sx={{ mt: .75, py: 0, flex: "0 0 auto", "& .MuiAlert-message": { py: .35, fontSize: 11 } }}>{error}</Alert> : null}

          <Box sx={{ flex: 1, minHeight: 0, mt: .35, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
            {loading && !analytics && !acquisition ? (
              <WebProfitSpinner size={30} />
            ) : !lensRows.length ? (
              <Box sx={{ textAlign: "center", color: "text.secondary" }}>
                <RouteRoundedIcon sx={{ fontSize: 38, opacity: .4 }} />
                <Typography variant="body2" sx={{ mt: .5 }}>Todavía no hay datos para esta lente.</Typography>
              </Box>
            ) : lens === "billing" ? (
              <BillingJourneyView billing={analytics?.billingJourney || {}} />
            ) : lens === "friction" ? (
              <FrictionLandscape rows={lensRows} selectedKey={selected?.key} onSelect={setSelectedKey} />
            ) : (
              <FlowChart rows={lensRows} selectedKey={selected?.key} onSelect={setSelectedKey} mode={mode} />
            )}
          </Box>

          {lens !== "billing" ? <Box sx={{ flex: "0 0 auto", borderTop: `1px solid ${BRAND.border}`, pt: .65 }}>
            <InsightRibbon analytics={analytics} acquisition={acquisition} lensRows={lensRows} lens={lens} />
          </Box> : null}
        </Box>

        <Box sx={{ minWidth: 0, minHeight: 0, display: { xs: "none", lg: "block" }, p: 1.25, borderLeft: `1px solid ${BRAND.border}`, bgcolor: "rgba(var(--wp-panel-ink-rgb,245,245,245),.012)" }}>
          {lens === "billing" ? <BillingJourneyInspector billing={analytics?.billingJourney || {}} /> : <Inspector node={selected} lens={lens} mode={mode} analytics={analytics} acquisition={acquisition} />}
        </Box>
      </Paper>

      <Paper elevation={0} sx={{ px: 1, py: .55, borderRadius: 3, border: `1px solid ${BRAND.border}`, bgcolor: BRAND.surface, flex: "0 0 auto" }}>
        <Stack direction="row" spacing={1.35} alignItems="center" sx={{ overflowX: "auto", scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}>
          <Stack direction="row" spacing={.45} alignItems="center" sx={{ minWidth: "max-content" }}><StorefrontRoundedIcon sx={{ fontSize: 15, color: BRAND.orange }} /><Typography variant="caption" sx={{ fontWeight: 900 }}>Lead → cuenta</Typography><Typography variant="caption" sx={{ color: "text.secondary" }}>{formatDecimal(acqOverview.registrationSuccessRate)}%</Typography></Stack>
          <Stack direction="row" spacing={.45} alignItems="center" sx={{ minWidth: "max-content" }}><RocketLaunchRoundedIcon sx={{ fontSize: 15, color: BRAND.green }} /><Typography variant="caption" sx={{ fontWeight: 900 }}>Activación</Typography><Typography variant="caption" sx={{ color: "text.secondary" }}>{formatDecimal(overview.activationRate)}%</Typography></Stack>
          <Stack direction="row" spacing={.45} alignItems="center" sx={{ minWidth: "max-content" }}><WarningAmberRoundedIcon sx={{ fontSize: 15, color: BRAND.amber }} /><Typography variant="caption" sx={{ fontWeight: 900 }}>Frenados</Typography><Typography variant="caption" sx={{ color: "text.secondary" }}>{formatNumber(overview.atRisk)}</Typography></Stack>
          <Stack direction="row" spacing={.45} alignItems="center" sx={{ minWidth: "max-content" }}><CheckCircleRoundedIcon sx={{ fontSize: 15, color: BRAND.green }} /><Typography variant="caption" sx={{ fontWeight: 900 }}>Tiempo activación</Typography><Typography variant="caption" sx={{ color: "text.secondary" }}>{formatDuration(overview.medianActivationMs)}</Typography></Stack>
        </Stack>
      </Paper>


      <LeadCrm
        state={leadState}
        search={leadSearch}
        onSearch={(value) => { setLeadSearch(value); setLeadPage(1); }}
        status={leadStatus}
        onStatus={(value) => { setLeadStatus(value); setLeadPage(1); }}
        onPage={setLeadPage}
      />

      <ClientUsageReport
        projects={projects}
        project={project}
        onProject={(nextProject) => { setProject(nextProject); setUsageUser(""); }}
        user={usageUser}
        onUser={setUsageUser}
        days={days}
        state={clientUsageState}
        onReload={loadClientUsage}
      />
    </Box>
  );
}
